import React, { useState, useMemo } from "react";
import {
  Box,
  Grid,
  Typography,
  Paper,
  Popover,
  List,
  ListItem,
  ListItemText,
  IconButton,
  Tooltip,
} from "@mui/material";
import {
  startOfWeek,
  addDays,
  startOfMonth,
  endOfMonth,
  isSameDay as fnsIsSameDay,
  format,
  getISOWeek
} from 'date-fns';
import { sv } from 'date-fns/locale';
import { MoreVert as MoreVertIcon } from '@mui/icons-material';
import { useDroppable } from "@dnd-kit/core";
import { CalendarItem, eventOccursOnDay } from "./calendarModel";
import DraggableEvent from "./DraggableEvent";

const getWeekDays = (date: Date): Date[] => {
  const start = startOfWeek(date, { weekStartsOn: 1 });
  return Array.from({ length: 7 }, (_, i) => addDays(start, i));
};

const getMonthDays = (date: Date): Date[] => {
  const firstDayOfMonth = startOfMonth(date);
  const lastDayOfMonth = endOfMonth(date);

  const startDate = startOfWeek(firstDayOfMonth, { weekStartsOn: 1 });
  const endDate = startOfWeek(lastDayOfMonth, { weekStartsOn: 1 });
  const endDateWithFullWeek = addDays(endDate, 6);

  const days: Date[] = [];
  let currentDate = startDate;

  while (currentDate <= endDateWithFullWeek) {
    days.push(new Date(currentDate));
    currentDate = addDays(currentDate, 1);
  }

  return days;
};

const organizeIntoWeeks = (days: Date[]): Date[][] => {
  const weeks: Date[][] = [];
  for (let i = 0; i < days.length; i += 7) {
    weeks.push(days.slice(i, i + 7));
  }
  return weeks;
};

const formatShortWeekDay = (date: Date): string => {
  return format(date, 'EEE', { locale: sv });
};

const formatShortDate = (date: Date): string => {
  return format(date, 'd', { locale: sv });
};

const formatTime = (date: Date): string => {
  return format(date, 'HH:mm', { locale: sv });
};

// Droppable day cell for month view
const DroppableDayCell: React.FC<{
  day: Date;
  children: React.ReactNode;
  sx?: object;
}> = ({ day, children, sx }) => {
  const dateStr = format(day, 'yyyy-MM-dd');
  const { isOver, setNodeRef } = useDroppable({
    id: `month-${dateStr}`,
    data: {
      id: `month-${dateStr}`,
      date: day,
      hour: -1, // Signal to preserve original time, only change the date
      minute: -1,
    },
  });

  return (
    <Grid
      ref={setNodeRef}
      item
      xs
      sx={{
        ...sx,
        ...(isOver ? {
          outline: "2px solid",
          outlineColor: "primary.main",
          outlineOffset: "-2px",
        } : {}),
      }}
    >
      {children}
    </Grid>
  );
};

interface MonthViewProps {
  viewDate: Date;
  events: CalendarItem[];
  onEventClick: (event: React.MouseEvent<HTMLElement>, calendarItem: CalendarItem) => void;
  onEventHover?: (event: React.MouseEvent<HTMLElement>, calendarItem: CalendarItem) => void;
  onEventLeave?: () => void;
  renderEventIcon?: (event: CalendarItem) => React.ReactNode;
}

const MAX_EVENTS_PER_DAY = 3;

const MonthView: React.FC<MonthViewProps> = ({
  viewDate,
  events,
  onEventClick,
  onEventHover,
  onEventLeave,
  renderEventIcon
}) => {
  const [moreEventsAnchorEl, setMoreEventsAnchorEl] = useState<HTMLElement | null>(null);
  const [selectedDayEvents, setSelectedDayEvents] = useState<CalendarItem[]>([]);
  const [selectedDay, setSelectedDay] = useState<Date | null>(null);

  const monthDays = useMemo(() => getMonthDays(viewDate), [viewDate]);
  const weeks = useMemo(() => organizeIntoWeeks(monthDays), [monthDays]);

  const getEventsForDay = (day: Date): CalendarItem[] => {
    // Heldagshändelser först så att de alltid syns, därefter starttid
    return events.filter(event => eventOccursOnDay(event, day))
      .sort((a, b) =>
        Number(b.isAllDay ?? false) - Number(a.isAllDay ?? false) ||
        a.start.getTime() - b.start.getTime()
      );
  };

  const handleMoreEventsClick = (event: React.MouseEvent<HTMLElement>, day: Date) => {
    const eventsForDay = getEventsForDay(day);
    setMoreEventsAnchorEl(event.currentTarget);
    setSelectedDayEvents(eventsForDay);
    setSelectedDay(day);
  };

  const handleMoreEventsClose = () => {
    setMoreEventsAnchorEl(null);
  };

  const cellHeight = weeks.length > 5 ? 90 : weeks.length < 5 ? 140 : 120;

  return (
    <Box sx={{ height: "calc(100vh - 200px)", overflow: "auto" }}>
      {/* Week day headers */}
      <Box sx={{ display: "flex" }}>
        <Box
          sx={{
            width: 36,
            flexShrink: 0,
            textAlign: "center",
            p: 1,
            borderBottom: "1px solid #e0e0e0",
            borderRight: "1px solid #e0e0e0",
            position: "sticky",
            top: 0,
            zIndex: 1,
            bgcolor: "background.paper",
          }}
        >
          <Typography variant="caption" color="text.secondary" fontWeight="medium">
            v.
          </Typography>
        </Box>
        <Grid container sx={{ flex: 1 }}>
          {getWeekDays(new Date()).map(day => (
            <Grid
              key={day.toString()}
              item
              xs
              sx={{
                textAlign: "center",
                p: 1,
                borderBottom: "1px solid #e0e0e0",
                position: "sticky",
                top: 0,
                zIndex: 1,
                bgcolor: "background.paper"
              }}
            >
              <Typography variant="body2" fontWeight="medium">
                {formatShortWeekDay(day)}
              </Typography>
            </Grid>
          ))}
        </Grid>
      </Box>

      {/* Calendar days */}
      {weeks.map((week, weekIndex) => (
        <Box key={weekIndex} sx={{ display: "flex" }}>
          <Box
            sx={{
              width: 36,
              flexShrink: 0,
              height: `${cellHeight}px`,
              borderRight: "1px solid #e0e0e0",
              borderBottom: "1px solid #e0e0e0",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              bgcolor: "background.paper",
            }}
          >
            <Typography
              variant="caption"
              fontWeight="bold"
              sx={{ color: "text.secondary" }}
            >
              {getISOWeek(week[0])}
            </Typography>
          </Box>
          <Grid container sx={{ flex: 1 }}>
            {week.map(day => {
            const eventsForDay = getEventsForDay(day);
            const isCurrentMonth = day.getMonth() === viewDate.getMonth();
            const isToday = fnsIsSameDay(day, new Date());
            const hasMoreEvents = eventsForDay.length > MAX_EVENTS_PER_DAY;

            return (
              <DroppableDayCell
                key={day.toString()}
                day={day}
                sx={{
                  height: `${cellHeight}px`,
                  border: "1px solid #e0e0e0",
                  p: 0,
                  bgcolor: isToday ? "primary.light" : (isCurrentMonth ? "background.paper" : "#f5f5f5"),
                  color: isToday ? "white" : "inherit",
                  overflow: "hidden",
                  display: "flex",
                  flexDirection: "column"
                }}
              >
                {/* Date */}
                <Typography
                  variant="caption"
                  sx={{
                    p: 0.5,
                    display: "block",
                    textAlign: "right",
                    fontWeight: isToday ? "bold" : (isCurrentMonth ? "normal" : "light"),
                    opacity: isCurrentMonth ? 1 : 0.6
                  }}
                >
                  {formatShortDate(day)}
                </Typography>

                {/* Events */}
                <Box sx={{
                  px: 0.5,
                  overflow: "hidden",
                  flexGrow: 1,
                  display: "flex",
                  flexDirection: "column"
                }}>
                  {eventsForDay.slice(0, MAX_EVENTS_PER_DAY).map((event, index) => (
                    <DraggableEvent key={`${event.id}-${index}`} event={event}>
                      <Paper
                        elevation={0}
                        sx={{
                          mb: 0.5,
                          p: 0.5,
                          bgcolor: event.color || (isToday ? "primary.dark" : "primary.light"),
                          color: "white",
                          cursor: "grab",
                          borderRadius: 1,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                          display: "flex",
                          alignItems: "center",
                          gap: 0.5,
                          '&:hover': {
                            bgcolor: event.color ? `${event.color}dd` : "primary.main",
                            opacity: 0.9
                          }
                        }}
                        onClick={(e) => onEventClick(e, event)}
                        onMouseEnter={onEventHover ? (e) => onEventHover(e, event) : undefined}
                        onMouseLeave={onEventLeave}
                      >
                        {renderEventIcon && (
                          <Box sx={{ flexShrink: 0, display: "flex", alignItems: "center" }}>
                            {renderEventIcon(event)}
                          </Box>
                        )}
                        <Typography variant="caption" noWrap sx={{ fontSize: "11px" }}>
                          {event.isAllDay ? event.title : `${formatTime(event.start)} ${event.title}`}
                        </Typography>
                      </Paper>
                    </DraggableEvent>
                  ))}

                  {/* Show "more events" button */}
                  {hasMoreEvents && (
                    <Box
                      sx={{
                        mt: 'auto',
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        mb: 0.5
                      }}
                    >
                      <Tooltip title={`Visa alla ${eventsForDay.length} händelser`}>
                        <IconButton
                          size="small"
                          onClick={(e) => handleMoreEventsClick(e, day)}
                          sx={{
                            p: 0.5,
                            bgcolor: isToday ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.05)',
                            '&:hover': {
                              bgcolor: isToday ? 'rgba(255,255,255,0.3)' : 'rgba(0,0,0,0.1)'
                            }
                          }}
                        >
                          <MoreVertIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Typography variant="caption" sx={{ ml: 0.5, fontSize: "10px" }}>
                        +{eventsForDay.length - MAX_EVENTS_PER_DAY} till
                      </Typography>
                    </Box>
                  )}
                </Box>
              </DroppableDayCell>
            );
          })}
          </Grid>
        </Box>
      ))}

      {/* Popover for showing more events */}
      <Popover
        open={Boolean(moreEventsAnchorEl)}
        anchorEl={moreEventsAnchorEl}
        onClose={handleMoreEventsClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
      >
        <Box sx={{ p: 1, maxWidth: 300 }}>
          <Typography variant="subtitle2" sx={{ p: 1 }}>
            Alla händelser {selectedDay &&
              format(selectedDay, 'd MMMM', { locale: sv })}
          </Typography>
          <List dense>
            {selectedDayEvents.map((event) => (
              <ListItem
                key={event.id}
                sx={{
                  borderLeft: `4px solid ${event.color || '#2196f3'}`,
                  mb: 0.5,
                  cursor: 'pointer',
                  '&:hover': {
                    bgcolor: 'rgba(0,0,0,0.04)'
                  }
                }}
                onClick={(e) => {
                  handleMoreEventsClose();
                  onEventClick(e, event);
                }}
                onMouseEnter={onEventHover ? (e) => onEventHover(e, event) : undefined}
                onMouseLeave={onEventLeave}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, width: "100%" }}>
                  {renderEventIcon && renderEventIcon(event)}
                  <ListItemText
                    primary={event.title}
                    secondary={event.isAllDay ? "Heldag" : formatTime(event.start)}
                    primaryTypographyProps={{
                      variant: 'body2',
                      fontWeight: 'medium'
                    }}
                    secondaryTypographyProps={{
                      variant: 'caption'
                    }}
                  />
                </Box>
              </ListItem>
            ))}
          </List>
        </Box>
      </Popover>
    </Box>
  );
};

export default MonthView;
